import map from 'lodash/map';
import { ExternalSourceType, ExternalSourceTypes } from 'src/actions/integrations/commonTypes';

// Maximum number of the rows we allow in the 'Search Query Attributes' and 'Search Result Attributes' lists
export const MaxQueryAttributeCount = 25;

export const SHOPIFY_APPSTORE_REDIRECT_URL = 'https://apps.shopify.com/gladly';

export const IntegrationDisplayLabels: Readonly<Record<ExternalSourceTypes, string>> = {
  [ExternalSourceTypes.Shopify]: 'Shopify',
  [ExternalSourceTypes.Smile]: 'Smile',
  [ExternalSourceTypes.Magento]: 'Magento',
  [ExternalSourceTypes.LoopReturns]: 'Loop Returns',
  [ExternalSourceTypes.Klaviyo]: 'Klaviyo',
  [ExternalSourceTypes.Segment]: 'Segment',
  [ExternalSourceTypes.BigCommerce]: 'BigCommerce',
  [ExternalSourceTypes.AwsEventBridge]: 'AWS EventBridge',
  [ExternalSourceTypes.Zaius]: 'Optimizely',
  [ExternalSourceTypes.Recharge]: 'Recharge',
  [ExternalSourceTypes.Qualtrics]: 'Qualtrics',
  [ExternalSourceTypes.Yotpo]: 'Yotpo',
  [ExternalSourceTypes.Ordergroove]: 'Ordergroove',
  [ExternalSourceTypes.Custom]: 'Custom',
};

export const CustomerLookupSupport: Readonly<Record<ExternalSourceType, boolean>> = {
  [ExternalSourceTypes.Shopify]: true,
  [ExternalSourceTypes.Smile]: true,
  [ExternalSourceTypes.Magento]: true,
  [ExternalSourceTypes.LoopReturns]: false,
  [ExternalSourceTypes.Custom]: true,
  [ExternalSourceTypes.Klaviyo]: false,
  [ExternalSourceTypes.Segment]: false,
  [ExternalSourceTypes.BigCommerce]: true,
  [ExternalSourceTypes.AwsEventBridge]: false,
  [ExternalSourceTypes.Zaius]: false,
  [ExternalSourceTypes.Recharge]: true,
  [ExternalSourceTypes.Qualtrics]: false,
  [ExternalSourceTypes.Yotpo]: true,
  [ExternalSourceTypes.Ordergroove]: true,
};

export const CustomerSearchSupport: Readonly<Record<ExternalSourceType, boolean>> = {
  [ExternalSourceTypes.Shopify]: true,
  [ExternalSourceTypes.Smile]: true,
  [ExternalSourceTypes.Magento]: true,
  [ExternalSourceTypes.LoopReturns]: false,
  [ExternalSourceTypes.Custom]: true,
  [ExternalSourceTypes.Klaviyo]: false,
  [ExternalSourceTypes.Segment]: false,
  [ExternalSourceTypes.BigCommerce]: true,
  [ExternalSourceTypes.AwsEventBridge]: false,
  [ExternalSourceTypes.Zaius]: false,
  [ExternalSourceTypes.Recharge]: true,
  [ExternalSourceTypes.Qualtrics]: false,
  [ExternalSourceTypes.Yotpo]: true,
  [ExternalSourceTypes.Ordergroove]: false,
};

export const ActionsSupport: Readonly<Record<ExternalSourceType, boolean>> = {
  [ExternalSourceTypes.Shopify]: true,
  [ExternalSourceTypes.Smile]: false,
  [ExternalSourceTypes.Magento]: false,
  [ExternalSourceTypes.LoopReturns]: false,
  [ExternalSourceTypes.Custom]: true,
  [ExternalSourceTypes.Klaviyo]: false,
  [ExternalSourceTypes.Segment]: false,
  [ExternalSourceTypes.BigCommerce]: true,
  [ExternalSourceTypes.AwsEventBridge]: false,
  [ExternalSourceTypes.Zaius]: false,
  [ExternalSourceTypes.Recharge]: false,
  [ExternalSourceTypes.Qualtrics]: false,
  [ExternalSourceTypes.Yotpo]: false,
  [ExternalSourceTypes.Ordergroove]: true,
};

export const RequiredSearchQueryFieldNames: Readonly<Record<ExternalSourceType, string[]>> = {
  [ExternalSourceTypes.Shopify]: ['email'],
  [ExternalSourceTypes.Smile]: ['email'],
  [ExternalSourceTypes.Magento]: ['email'],
  [ExternalSourceTypes.LoopReturns]: [],
  [ExternalSourceTypes.Custom]: [],
  [ExternalSourceTypes.Klaviyo]: [],
  [ExternalSourceTypes.Segment]: [],
  [ExternalSourceTypes.BigCommerce]: ['email'],
  [ExternalSourceTypes.AwsEventBridge]: [],
  [ExternalSourceTypes.Zaius]: [],
  [ExternalSourceTypes.Recharge]: ['email'],
  [ExternalSourceTypes.Qualtrics]: [],
  [ExternalSourceTypes.Yotpo]: ['email'],
  [ExternalSourceTypes.Ordergroove]: [],
};

export const RequiredSearchResultFieldNames: Readonly<Record<ExternalSourceType, string[]>> = {
  [ExternalSourceTypes.Shopify]: [],
  [ExternalSourceTypes.Smile]: [],
  [ExternalSourceTypes.Magento]: [],
  [ExternalSourceTypes.LoopReturns]: [],
  [ExternalSourceTypes.Custom]: [],
  [ExternalSourceTypes.Klaviyo]: [],
  [ExternalSourceTypes.Segment]: [],
  [ExternalSourceTypes.BigCommerce]: [],
  [ExternalSourceTypes.AwsEventBridge]: [],
  [ExternalSourceTypes.Zaius]: [],
  [ExternalSourceTypes.Recharge]: [],
  [ExternalSourceTypes.Qualtrics]: [],
  [ExternalSourceTypes.Yotpo]: [],
  [ExternalSourceTypes.Ordergroove]: [],
};

export const AutoLinkSupportForEmailAndPhone: ReadonlyArray<ExternalSourceTypes> = [
  ExternalSourceTypes.Shopify,
  ExternalSourceTypes.BigCommerce,
  ExternalSourceTypes.Magento,
  ExternalSourceTypes.Custom,
];

const createListOptions = rawList =>
  map(rawList, (label, value) => ({
    label,
    value,
  }));

const AWS_SUPPORTED_REGIONS: Readonly<Record<string, string>> = {
  'us-east-2': 'US East (Ohio)',
  'us-east-1': 'US East (N. Virginia)',
  'us-west-1': 'US West (N. California)',
  'us-west-2': 'US West (Oregon)',
  'ap-south-1': 'Asia Pacific (Mumbai)',
  'ap-southeast-1': 'Asia Pacific (Singapore)',
  'ap-southeast-2': 'Asia Pacific (Sydney)',
  'ap-northeast-1': 'Asia Pacific (Tokyo)',
  'ca-central-1': 'Canada (Central)',
  'eu-central-1': 'Europe (Frankfurt)',
  'eu-west-1': 'Europe (Ireland)',
  'eu-west-2': 'Europe (London)',
  'eu-west-3': 'Europe (Paris)',
  'eu-north-1': 'Europe (Stockholm)',
  'me-south-1': 'Middle East (Bahrain)',
  'sa-east-1': 'South America (Sao Paulo)',
};

const STREAMING_SUPPORTED_ENTITIES: Readonly<Record<string, string>> = {
  AGENT_AVAILABILITY: 'Agent Availability',
  AGENT_STATUS: 'Agent Status',
  CONTACT: 'Contact',
  CONVERSATION: 'Conversation',
  CUSTOMER: 'Customer',
  EXPORT_JOB: 'Export',
  PAYMENT_REQUEST: 'Payment Request',
  TASK: 'Task',
};

export const SUPPORTED_REGIONS = createListOptions(AWS_SUPPORTED_REGIONS);
export const SUPPORTED_ENTITIES = createListOptions(STREAMING_SUPPORTED_ENTITIES);
